import type { WithStyles } from '@material-ui/core';
import { withStyles } from '@material-ui/core';
import _ from 'lodash';
import { action, computed, observable, toJS } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import type ICoordinate from '../interfaces/coordinates';
import DrawingPath from '../path';
import styles from './styles';

interface IProps extends WithStyles<typeof styles> {
  value?: string | null;
  readOnly: boolean;
  onChange: (value: string | null) => void;
  strokeColor: string;
  strokeWidth: number;
}

@observer
class Drawing extends React.Component<IProps> {
  @observable isDrawing: boolean = false;
  @observable paths: Array<ICoordinate[]> = [];
  @observable selectedIndex: number = -1;

  drawingRef: React.RefObject<HTMLDivElement>;
  onDebounce: () => void;

  constructor(props: IProps) {
    super(props);
    this.setPoints();
    this.drawingRef = React.createRef();
    this.onDebounce = _.throttle(this.updateValue, 1000);
  }

  componentDidMount() {
    //for ios devices to prevent scrolling when drawing
    this.drawingRef.current!.addEventListener('pointerdown', this.onStart, false);
    this.drawingRef.current!.addEventListener('pointermove', this.onMove, false);

    window.addEventListener('pointerup', this.onRelease);
  }

  componentWillUnmount() {
    this.drawingRef.current!.removeEventListener('pointerdown', this.onStart, false);
    this.drawingRef.current!.removeEventListener('pointermove', this.onMove, false);

    window.removeEventListener('pointerup', this.onRelease);
  }

  componentDidUpdate(prevProps: IProps) {
    if (this.props.value !== prevProps.value) {
      this.setPoints();
    }
  }

  @computed
  get isPathsEmpty() {
    return this.paths.length === 0;
  }

  @computed
  get newValue(): string {
    return JSON.stringify(toJS(this.paths));
  }

  @computed
  get className(): string {
    const { classes, readOnly } = this.props;
    return `${classes.drawingBox} ${readOnly ? classes.readonly : ''}`;
  }

  @action
  setPoints(): void {
    this.paths = this.props.value ? JSON.parse(this.props.value) : [];
  }

  @action.bound
  onStart(e: React.MouseEvent | React.TouchEvent): void {
    if (!!e) {
      e.preventDefault();
      e.stopPropagation();
    }

    this.isDrawing = true;
    this.paths.push([this.getCoordinates(e)]);

    this.selectedIndex = this.paths.length - 1;
  }

  @action.bound
  onMove(e: React.MouseEvent | React.TouchEvent): void {
    if (!!e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const { isDrawing, selectedIndex } = this;

    if (!isDrawing) {
      return;
    }

    this.paths[selectedIndex].push(this.getCoordinates(e));
  }

  @action.bound
  onRelease(e: React.MouseEvent | React.TouchEvent): void {
    const { readOnly, value } = this.props;

    this.isDrawing = false;

    if (!readOnly && !this.isPathsEmpty && value !== this.newValue) {
      this.onDebounce();
    }
  }

  getCoordinates(event: React.MouseEvent | React.TouchEvent) {
    const boundingRect = this.drawingRef.current!.getBoundingClientRect();
    const e = 'touches' in event ? event.touches[0] : event;
    const xCoordinate = e.clientX - boundingRect.left;
    const yCoordinate = e.clientY - boundingRect.top;
    return { x: xCoordinate, y: yCoordinate };
  }

  updateValue(): void {
    if (!!this.isPathsEmpty) {
      return;
    }
    const val = JSON.stringify(toJS(this.paths));
    this.props.onChange(val);
  }

  render() {
    const { paths, isPathsEmpty, onStart, onMove, onRelease, drawingRef } = this;
    const { strokeColor, strokeWidth, readOnly, classes } = this.props;

    const signText =
      !readOnly && !!isPathsEmpty ? (
        <text
          x="50%"
          y="50%"
          fill="grey"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={50}
        >
          Sign here
        </text>
      ) : null;

    const content = paths.map((line, index) => {
      return (
        <DrawingPath
          key={`path_${index}`}
          points={line}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      );
    });

    return (
      <div
        id="svg-canvas"
        className={this.className}
        ref={drawingRef}
        onMouseDown={onStart}
        onMouseMove={onMove}
        onTouchEnd={onRelease}
      >
        <svg className={classes.svgDrawing}>
          {signText}
          {content}
        </svg>
      </div>
    );
  }
}

export default withStyles(styles, { name: 'Signature-Drawing' })(Drawing);
